/* eslint-env browser */
import { createAction, createReducer } from '@reduxjs/toolkit'
import { createSelector } from 'reselect'
import get from 'lodash/get'
import sumBy from 'lodash/sumBy'
import isEmpty from 'lodash/isEmpty'
import find from 'lodash/find'
import map from 'lodash/map'
import omit from 'lodash/omit'
import groupBy from 'lodash/groupBy'
import reduce from 'lodash/reduce'
import first from 'lodash/first'
import { getCartCheckoutLabel } from './content'
import {
  cartActionCreator,
  addToCart,
  clearCart,
  incrementLineItemQuantity,
  decrementLineItemQuantity,
  removeFromCart,
  updateCartFromShopify,
  syncCartWithShopify
} from '../actions/cart'

// Helper function to optimistically update quantity
function optimisticallyUpdateQuantity (state, variantId, { numberToAdd, quantity }) {
  const lineItems = get(state, ['content', 'lineItems'])
  if (!lineItems || !find(lineItems, ({ variant }) => variant.id === variantId)) {
    return state.content
  }
  return {
    ...omit(state.content, ['lineItems']),
    lineItems: map(lineItems, lineItem => {
      if (lineItem.variant.id === variantId) {
        return {
          ...lineItem,
          quantity: Math.max(0, numberToAdd ? lineItem.quantity + numberToAdd : quantity)
        }
      }
      return lineItem
    })
  }
}

export const serverErrorActionCreator = createAction('page/serverError')

// Add actions for cart error handling
export const setCartError = createAction('cart/setError')
export const clearCartError = createAction('cart/clearError')
export const setCartBusy = createAction('cart/setBusy')

// Initial state for the cart
const cartInitialState = {
  content: {
    id: null,
    lineItems: [],
    checkoutUrl: null,
    totalPrice: { amount: 0 },
    subtotalPrice: { amount: 0 },
    lineItemsSubtotalPrice: { amount: 0 },
    completedAt: null
  },
  busy: false,
  error: null,
  isLoading: false,
  lastUpdated: null,
  loadAttempts: 0
}

// Cart reducer
// Modified sections of the cart reducer to properly handle line items
const cartReducer = createReducer(cartInitialState, (builder) => {
  builder
    // Handle cart action creator pending
    .addCase(`${cartActionCreator.toString()}_PENDING`, (state) => {
      state.busy = true
      state.error = null
    })

    .addCase(setCartBusy, (state, action) => {
      state.busy = action.payload
    })

    // Handle cart action creator fulfilled
    .addCase(`${cartActionCreator.toString()}_FULFILLED`, (state, action) => {
      state.content = action.payload || null
      state.busy = false
      state.error = null
      state.lastUpdated = Date.now()
    })

    // Handle cart action creator rejected
    .addCase(`${cartActionCreator.toString()}_REJECTED`, (state, action) => {
      state.busy = false
      state.error = action.payload
    })

    // Handle clear cart action
    .addCase(clearCart, (state) => {
      state.content = null
      state.isLoading = false
      state.busy = false
      state.error = null
    })

    // Handle Shopify cart sync start
    .addCase(syncCartWithShopify, (state) => {
      state.isLoading = true
      state.loadAttempts += 1
    })

    // Handle increment line item quantity
    .addCase(incrementLineItemQuantity, (state, action) => {
      // When quantity is updated, don't show loading state but do mark as busy
      state.busy = true

      // FIXED: More careful handling of optimistic updates
      // Only update if the item actually exists in the cart
      const lineItems = state.content?.lineItems || []
      const itemExists = lineItems.some(item => item.variant.id === action.payload)

      if (itemExists) {
        state.content = optimisticallyUpdateQuantity(state, action.payload, { numberToAdd: 1 })
      }
    })

    // Handle decrement line item quantity
    .addCase(decrementLineItemQuantity, (state, action) => {
      // When quantity is updated, don't show loading state but do mark as busy
      state.busy = true

      // FIXED: More careful handling of optimistic updates
      // Only update if the item actually exists in the cart
      const lineItems = state.content?.lineItems || []
      const itemExists = lineItems.some(item => item.variant.id === action.payload)

      if (itemExists) {
        state.content = optimisticallyUpdateQuantity(state, action.payload, { numberToAdd: -1 })
      }
    })

    // Handle add to cart
    .addCase(addToCart, (state, action) => {
      state.busy = true
      // Leave the detailed implementation to the optimistic update in middleware
    })

    .addCase(removeFromCart, (state, action) => {
      // Mark cart as busy AND loading during removal
      state.busy = true
      state.isLoading = true // This is crucial for showing the loading state

      // If we have line items, filter out the one to be removed
      if (state.content && state.content.lineItems) {
        const lineItemId = action.payload
        state.content.lineItems = state.content.lineItems.filter(item => item.id !== lineItemId)
      }
    })

    // Handle update cart from Shopify
    .addCase(updateCartFromShopify, (state, action) => {
      // Check if there's an error in the Shopify response
      if (action.payload && action.payload.data && action.payload.data.cartLinesAdd && action.payload.data.cartLinesAdd.userErrors && action.payload.data.cartLinesAdd.userErrors.length > 0) {
        state.error = action.payload
        state.busy = false
        state.isLoading = false
        return
      }

      // FIXED: Better handling of the cart content
      // Only update if we have valid cart data to prevent empty carts
      if (action.payload) {
        // Ensure numeric values are properly parsed
        state.content = {
          ...action.payload,
          totalPrice: action.payload.totalPrice ? {
            amount: parseFloat(action.payload.totalPrice.amount || 0),
            currencyCode: action.payload.totalPrice.currencyCode || 'USD'
          } : { amount: 0, currencyCode: 'USD' },
          subtotalPrice: action.payload.subtotalPrice ? {
            amount: parseFloat(action.payload.subtotalPrice.amount || 0),
            currencyCode: action.payload.subtotalPrice.currencyCode || 'USD'
          } : { amount: 0, currencyCode: 'USD' },
          lineItemsSubtotalPrice: action.payload.lineItemsSubtotalPrice ? {
            amount: parseFloat(action.payload.lineItemsSubtotalPrice.amount || 0),
            currencyCode: action.payload.lineItemsSubtotalPrice.currencyCode || 'USD'
          } : { amount: 0, currencyCode: 'USD' },
          lineItems: (action.payload.lineItems || []).map(item => ({
            ...item,
            quantity: parseInt(item.quantity || 0, 10),
            variant: item.variant ? {
              ...item.variant,
              price: item.variant.price ? {
                amount: parseFloat(item.variant.price.amount || 0),
                currencyCode: item.variant.price.currencyCode || 'USD'
              } : { amount: 0, currencyCode: 'USD' }
            } : null
          }))
        }
      } else if (state.content) {
        // If no payload but we have content, keep the current content
        // but mark it as no longer busy or loading
        state.content = {
          ...state.content,
          lineItems: state.content.lineItems || []
        }
      } else {
        // If no payload and no content, set to null
        state.content = null
      }

      // ALWAYS reset these states, even if there was an error
      state.busy = false
      state.isLoading = false
      state.error = null
      state.lastUpdated = Date.now()
    })

    // Handle explicitly setting cart error
    .addCase(setCartError, (state, action) => {
      state.error = action.payload
      state.busy = false
      state.isLoading = false
    })

    // Handle clearing cart error
    .addCase(clearCartError, (state) => {
      state.error = null
    })
})

// Selectors
export const getCartId = state => get(state, ['cart', 'content', 'id'])
export const isCartCompleted = state => get(state, ['cart', 'content', 'completedAt'])
export const isCartLoading = state => get(state, ['cart', 'isLoading'])
export const isCartBusy = state => get(state, ['cart', 'busy'])
export const getLastCartUpdate = state => get(state, ['cart', 'lastUpdated'])
export const getCartError = state => get(state, ['cart', 'error'])

export const getCartLineItemsCount = state => {
  const lineItems = getCartLineItems(state)
  if (!lineItems || !lineItems.length) return 0
  return sumBy(lineItems, item => parseInt(item.quantity || 0, 10)) || 0
}

export const getCartCheckoutUrl = state => get(state, ['cart', 'content', 'checkoutUrl'])

export const getCartTotalPriceAmount = state => {
  const amount = get(state, ['cart', 'content', 'totalPrice', 'amount'])
  return parseFloat(amount || 0)
}

export const getCartSubtotalPriceAmount = state => {
  const amount = get(state, ['cart', 'content', 'subtotalPrice', 'amount'])
  return parseFloat(amount || 0)
}

export const getCartLineItemsSubtotalPrice = state => {
  const amount = get(state, ['cart', 'content', 'lineItemsSubtotalPrice', 'amount'])
  return parseFloat(amount || 0)
}

export const isCartEmpty = state => isEmpty(getCartLineItems(state))

export const getCartDiscount = createSelector(
  getCartTotalPriceAmount,
  getCartLineItemsSubtotalPrice,
  (total, lineItemsAmount) => {
    return lineItemsAmount - total
  }
)

export const getCartLineItems = createSelector(
  state => get(state, ['cart', 'content', 'lineItems']),
  (lineItems) => {
    if (!lineItems) return []

    // group by VARIANT_ID + SELLING_PLAN_ID (or "oneTime")
    const buckets = groupBy(
      lineItems,
      item => {
        const variantId = get(item, 'variant.id')
        const planId = get(item, 'sellingPlan.id') || 'oneTime'
        return `${variantId}::${planId}`
      }
    )

    // collapse each bucket into a single line
    return reduce(buckets, (result, items) => {
      // deep-clone first item to avoid mutating original
      const merged = JSON.parse(JSON.stringify(first(items)))

      // sum all quantities
      merged.quantity = sumBy(items, it => parseInt(it.quantity, 10)) || 0

      // normalize price object
      if (merged.variant?.price) {
        merged.variant.price = {
          amount: parseFloat(merged.variant.price.amount || 0),
          currencyCode: merged.variant.price.currencyCode || 'USD'
        }
      }

      result.push(merged)
      return result
    }, [])
  }
)

export const getCartCheckoutLink = createSelector(
  getCartCheckoutUrl,
  getCartCheckoutLabel,
  (checkoutUrl, copy, activeBundle) => {
    let url = checkoutUrl
    // This will add the discount code to the package if one is active
    if (activeBundle) {
      url += `&discount=${activeBundle.discount_code}`
    }
    return {
      type: 'externalLink',
      text: copy,
      url
    }
  }
)

export default cartReducer
